import { Divider, IconDocument, Text } from 'braid-design-system';
import type { GraphQLInterfaceType } from 'graphql';
import { InlineCode } from 'scoobie';

import { SchemaSection } from '#src/components/SchemaSection/SchemaSection.tsx';
import { sortGraphqlEntities } from '#src/utils/sort.ts';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import { FieldDetails } from '../components/FieldDetails.tsx';
import { SchemaMarkdown } from '../components/SchemaMarkdown.tsx';
import { TypeSeenIn } from '../components/TypeSeenIn.tsx';
import { TypeSnippet } from '../components/TypeSnippet.tsx';
import { schema } from '../schema.ts';
import { useLocation } from 'react-router';

export const InterfaceSchemaPage = ({
  interfaceType,
  fieldName,
}: {
  interfaceType: GraphQLInterfaceType;
  fieldName?: string;
}) => {
  const implementations = schema.getImplementations(interfaceType).objects;

  return (
    <BasePage
      title={{
        children: <InlineCode weight="weak">{interfaceType.name}</InlineCode>,
        htmlTitle: interfaceType.name,
        stringTitle: interfaceType.name,
        subHeading: (
          <Text
            icon={<IconDocument />}
            size="small"
            weight="medium"
            tone="secondary"
          >
            Interface
          </Text>
        ),
      }}
      breadcrumbs={[
        { href: '/schema', title: 'Schema' },
        { href: useLocation().pathname, title: interfaceType.name },
      ]}
    >
      <SchemaMarkdown type={interfaceType.name} />

      <Divider />

      <SchemaSection heading="Fields" wrapper="usage-table">
        {Object.entries(interfaceType.getFields())
          .sort(sortGraphqlEntities)
          .map(([fieldNameKey, field]) => (
            <FieldDetails
              key={fieldNameKey}
              objectName={interfaceType.name}
              highlight={fieldName === fieldNameKey}
              field={field}
              deprecated={typeof field.deprecationReason === 'string'}
            />
          ))}
      </SchemaSection>

      <Divider />

      <SchemaSection heading="Implementations">
        {[...implementations].map((objectType) => (
          <TypeSnippet key={objectType.name} type={objectType} />
        ))}
      </SchemaSection>

      <Divider />

      <TypeSeenIn name={interfaceType.name} />
    </BasePage>
  );
};
